import { type FC, type ReactNode, type SubmitEventHandler } from 'react'
import { useFormContext } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import {
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Link,
  LoadingButton,
  Notification,
} from 'base-ui'

import { getEmailRegex } from '../../../base-form-handling/validate'
import { FriendlyCaptchaChallenge } from '../../../friendly-captcha/components/FriendlyCaptchaChallenge/FriendlyCaptchaChallenge'
import { TENANT } from '../../../tenant/tenantTypes'
import { type useApiSwrLoginInitiateRequestMutation } from '../api/useApiSwrLoginInitiateRequestMutation'

export type LoginRegistrationUserLoginFormData = {
  fcSolution: string
  username: string
}

type LoginRegistrationUserLoginEmailFormProps = {
  children: ReactNode
  forgotPasswordUrl: string
  hasUnsupportedNextPageError: boolean
  isFriendlyCaptchaDone: boolean
  loginInitiateRequestMutation: ReturnType<
    typeof useApiSwrLoginInitiateRequestMutation
  >
  onCaptchaDone: (fcSolution: string) => void
  onSubmit: SubmitEventHandler<HTMLFormElement>
  tenant: TENANT
}

export const LoginRegistrationUserLoginEmailForm: FC<
  LoginRegistrationUserLoginEmailFormProps
> = ({
  children,
  forgotPasswordUrl,
  hasUnsupportedNextPageError,
  isFriendlyCaptchaDone,
  loginInitiateRequestMutation,
  onCaptchaDone,
  onSubmit,
  tenant,
}) => {
  const intl = useIntl()
  const {
    formState: { errors, isSubmitting },
    register,
    watch,
  } = useFormContext<LoginRegistrationUserLoginFormData>()
  const username = watch('username')
  const emailRegex = getEmailRegex()
  const { required, ...usernameInputProps } = register('username', {
    pattern: {
      message: intl.formatMessage({ id: 'error.email.invalid' }),
      value: emailRegex,
    },
    required: intl.formatMessage({ id: 'form.error.input.missing' }),
  })
  const usernameError = errors.username?.message

  return (
    <form data-qa-id="loginModal" onSubmit={onSubmit}>
      {children}
      {new Set<TENANT>([TENANT.BE, TENANT.CH, TENANT.FR]).has(tenant) && (
        <p className="mb-5">
          {intl.formatMessage({ id: 'login.rebrandingInfo' })}
        </p>
      )}
      {hasUnsupportedNextPageError || loginInitiateRequestMutation.error ? (
        <Notification className="mb-3.5" icon severity="error">
          <FormattedMessage id="changeEmail.errorMessage.general" />
        </Notification>
      ) : null}
      <FormControl
        className="mb-3.5"
        error={Boolean(usernameError)}
        required={required}
        value={username}
      >
        <FormLabel htmlFor="login-modal-email">
          {intl.formatMessage({ id: 'general.label.email' })}
        </FormLabel>
        <Input
          id="login-modal-email"
          slotProps={{
            input: { 'data-qa-id': 'login-form-username' },
          }}
          {...usernameInputProps}
        />
        {usernameError ? (
          <FormHelperText data-qa-id="loginForm-eMail-error-msg">
            {usernameError}
          </FormHelperText>
        ) : null}
      </FormControl>
      <FriendlyCaptchaChallenge
        className="mb-2"
        doneCallback={onCaptchaDone}
        startMode="auto"
        whenThisPropChangesResetCaptcha={
          loginInitiateRequestMutation.data ??
          loginInitiateRequestMutation.error
        }
      />
      <LoadingButton
        className="w-full"
        data-qa-id="login-form-loginForm-submit-button"
        disabled={!isFriendlyCaptchaDone || isSubmitting}
        loading={isSubmitting}
        loadingPosition="end"
        type="submit"
      >
        <FormattedMessage id="general.label.subscribe" />
      </LoadingButton>
      <div className="mt-5 text-center">
        <Link
          className="font-medium"
          data-qa-id="login-form-forgot-password-link"
          href={forgotPasswordUrl}
        >
          <FormattedMessage id="general.link.label.password.forgotten" />
        </Link>
      </div>
    </form>
  )
}
